<template>
    <div class="sucursal-form-modern">
        <div class="form-scroll-area">
            <!-- Section 1: Legal Identity -->
            <div class="premium-form-card">
                <div class="card-badge-premium">Identidad Legal</div>
                <div class="grid-layout-premium">
                    <div class="input-group-premium">
                        <label>
                            <HashIcon :size="14" /> Expediente
                        </label>
                        <input type="text" v-model="form.numeroExpediente" placeholder="Ej: 223-32131" />
                    </div>
                    <div class="input-group-premium">
                        <label>
                            <FileTextIcon :size="14" /> Tomo
                        </label>
                        <input type="text" v-model="form.numeroTomo" placeholder="Ej: 47-A" />
                    </div>
                    <div class="input-group-premium full-col">
                        <label>
                            <BriefcaseIcon :size="14" /> Registro Mercantil
                        </label>
                        <input type="text" v-model="form.nombreRegistroMercantil"
                            placeholder="Oficina de registro oficial" />
                    </div>
                    <div class="input-group-premium">
                        <label>
                            <CalendarIcon :size="14" /> Nro / Año
                        </label>
                        <input type="text" v-model="form.numeroRegistro" placeholder="Ej: 2024" />
                    </div>
                    <div class="input-group-premium">
                        <label>
                            <FileUpIcon :size="14" /> Acta (PDF)
                        </label>
                        <div class="file-drop-zone" @click="fileInput.click()">
                            <FileUpIcon v-if="!selectedFile && !form.rutaActaRegistroPdf" :size="20" />
                            <CheckCircleIcon v-else :size="20" class="text-success" />
                            <span class="truncate">{{ selectedFile ? selectedFile.name : (form.rutaActaRegistroPdf ?
                                'Acta vinculada' : 'Vincular documento PDF') }}</span>
                            <input type="file" ref="fileInput" hidden accept=".pdf" @change="onFileSelected" />
                        </div>
                        <div v-if="form.rutaActaRegistroPdf && !selectedFile" class="existing-file-link mt-2">
                            <a :href="form.rutaActaRegistroPdf" target="_blank"
                                class="text-xs text-blue-500 hover:underline">
                                <EyeIcon :size="12" class="inline mr-1" /> Ver acta actual
                            </a>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Section 2: Operational Data -->
            <div class="premium-form-card mt-6">
                <div class="card-badge-premium operational">Operación y Marca</div>
                <div class="grid-layout-premium">
                    <div class="input-group-premium">
                        <label>
                            <StoreIcon :size="14" /> Tipo de Tienda
                        </label>
                        <select v-model="form.tipoTienda">
                            <option value="Expless">Expless</option>
                            <option value="Plus">Plus</option>
                            <option value="Mega">Mega</option>
                        </select>
                    </div>
                    <div class="input-group-premium">
                        <label>
                            <HashIcon :size="14" /> Código POS
                        </label>
                        <input type="text" v-model="form.numeroSucursal" placeholder="Ej: 101" />
                    </div>
                    <div class="input-group-premium full-col">
                        <label>
                            <InfoIcon :size="14" /> Nombre Comercial
                        </label>
                        <input type="text" v-model="form.nombreSucursal" placeholder="Nombre que verá el cliente" />
                    </div>
                </div>
            </div>

            <!-- Section 3: Localization & Contact -->
            <div class="premium-form-card mt-6">
                <div class="card-badge-premium localization">Ubicación y Contacto</div>
                <div class="grid-layout-premium">
                    <div class="input-group-premium full-col">
                        <label>
                            <MapPinIcon :size="14" /> Dirección Exacta
                        </label>
                        <textarea v-model="form.direccion" rows="2" placeholder="Av, Edificio, Local..."></textarea>
                    </div>
                    <div class="input-group-premium">
                        <label>
                            <GlobeIcon :size="14" /> Ciudad / Municipio
                        </label>
                        <input type="text" v-model="form.ciudad" placeholder="Ej: Caracas" />
                    </div>
                    <div class="input-group-premium">
                        <label>
                            <MapIcon :size="14" /> Estado
                        </label>
                        <input type="text" v-model="form.estadoDir" placeholder="Ej: Distrito Capital" />
                    </div>
                    <div class="input-group-premium">
                        <label>
                            <PhoneIcon :size="14" /> Teléfono
                        </label>
                        <input type="text" v-model="form.telefono" placeholder="+58..." />
                    </div>
                    <div class="input-group-premium">
                        <label>
                            <MailIcon :size="14" /> Email
                        </label>
                        <input type="email" v-model="form.email" placeholder="pos@tienda.com" />
                    </div>
                    <div class="input-group-premium full-col">
                        <label>
                            <NavigationIcon :size="14" /> Coordenadas GPS (Lat, Long)
                        </label>
                        <input type="text" v-model="form.coordenadasGps" placeholder="10.4806, -66.9036" @change="updateMapFromInput" />
                        <div id="sucursal-map" class="map-container-premium mt-3"></div>
                        <small class="text-muted text-xs mt-1">Arrastra el marcador para precisar la ubicación.</small>
                    </div>
                </div>
            </div>

            <!-- Section 4: Responsables -->
            <div class="premium-form-card mt-6">
                <div class="card-badge-premium staff">Personal Responsable</div>
                <div class="staff-selector-modern">
                    <div class="staff-search-box">
                        <SearchIcon :size="18" />
                        <input type="text" v-model="userSearch" placeholder="Buscar personal..." @input="fetchUsers" />
                    </div>
                    <div class="staff-list custom-scrollbar">
                        <div v-for="user in users" :key="user.usuarioId" class="staff-item-modern"
                            :class="{ active: form.responsableIds.includes(user.usuarioId) }"
                            @click="toggleUser(user.usuarioId)">
                            <div class="staff-avatar">
                                {{ user.nombre.charAt(0) }}
                            </div>
                            <div class="staff-info">
                                <span class="s-name">{{ user.nombre }}</span>
                                <span class="s-role">{{ user.cargo || 'Staff' }}</span>
                            </div>
                            <div class="selection-indicator">
                                <CheckCircleIcon :size="18" v-if="form.responsableIds.includes(user.usuarioId)" />
                                <div v-else class="indicator-dot"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Sticky Footer -->
        <div class="premium-form-footer">
            <button class="btn-ghost-premium" @click="$emit('cancel')">Cancelar</button>
            <button class="btn-primary-premium" @click="submit" :disabled="loading">
                <Loader2Icon v-if="loading" class="spinner-icon" :size="20" />
                <span v-else>{{ isEditing ? 'Actualizar Información' : 'Crear Punto de Venta' }}</span>
            </button>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue';
import {
    FileTextIcon, StoreIcon, UsersIcon, FileUpIcon,
    MapPinIcon, GlobeIcon, MapIcon, PhoneIcon, MailIcon,
    NavigationIcon, ArrowRightIcon, EyeIcon, CheckCircleIcon
} from 'lucide-vue-next';
import { UserService } from '@/services/UserService';

const props = defineProps({
    loading: Boolean,
    initialData: Object
});

const emit = defineEmits(['save', 'cancel']);

const isEditing = ref(false);
const form = ref({
    sucursalId: null,
    numeroExpediente: '',
    numeroTomo: '',
    nombreRegistroMercantil: '',
    numeroRegistro: '',
    tipoTienda: 'EXPRESS',
    numeroSucursal: '',
    nombreSucursal: '',
    direccion: '',
    ciudad: '',
    estadoDir: '',
    telefono: '',
    email: '',
    coordenadasGps: '',
    responsableIds: [],
    activo: true
});

const fileInput = ref(null);
const selectedFile = ref(null);
const userSearch = ref('');
const users = ref([]);

const fetchUsers = async () => {
    try {
        const res = await UserService.getUsuarios(userSearch.value, 1, 30);
        // Correctly mapping based on user provided data structure
        users.value = res?.datos?.items || res?.data?.datos?.items || [];
    } catch (error) {
        console.error(error);
    }
};

const toggleUser = (id) => {
    const index = form.value.responsableIds.indexOf(id);
    if (index > -1) {
        form.value.responsableIds.splice(index, 1);
    } else {
        form.value.responsableIds.push(id);
    }
};

const onFileSelected = (e) => {
    const file = e.target.files[0];
    if (file) selectedFile.value = file;
};

const submit = () => {
    emit('save', { ...form.value, file: selectedFile.value });
};

const initData = () => {
    if (props.initialData) {
        isEditing.value = true;
        Object.assign(form.value, props.initialData);
        // Map responsible objects to IDs if needed
        if (props.initialData.responsables) {
            form.value.responsableIds = props.initialData.responsables.map(r => r.usuarioId);
        }
    } else {
        isEditing.value = false;
    }
};

// Map Logic
let map = null;
let marker = null;
let geocoder = null;

const initMap = () => {
    if (typeof window.google === 'undefined') return;
    
    geocoder = new window.google.maps.Geocoder();
    
    let lat = 10.4806;
    let lng = -66.9036;
    
    if (form.value.coordenadasGps) {
        const parts = form.value.coordenadasGps.split(',');
        if (parts.length === 2) {
            lat = parseFloat(parts[0]);
            lng = parseFloat(parts[1]);
        }
    }

    const mapOptions = {
        center: { lat, lng },
        zoom: 15,
        disableDefaultUI: true,
        zoomControl: true,
    };

    const mapElement = document.getElementById('sucursal-map');
    if (!mapElement) return;

    map = new window.google.maps.Map(mapElement, mapOptions);
    marker = new window.google.maps.Marker({
        position: { lat, lng },
        map: map,
        draggable: true,
        title: "Ubicación del Punto de Venta"
    });

    marker.addListener('dragend', () => {
        const pos = marker.getPosition();
        form.value.coordenadasGps = `${pos.lat().toFixed(6)}, ${pos.lng().toFixed(6)}`;
    });

    map.addListener('click', (e) => {
        const pos = e.latLng;
        marker.setPosition(pos);
        form.value.coordenadasGps = `${pos.lat().toFixed(6)}, ${pos.lng().toFixed(6)}`;
    });
};

const updateMapFromInput = () => {
    if (!form.value.coordenadasGps || !marker || !map) return;
    const parts = form.value.coordenadasGps.split(',');
    if (parts.length === 2) {
        const lat = parseFloat(parts[0]);
        const lng = parseFloat(parts[1]);
        if (!isNaN(lat) && !isNaN(lng)) {
            const pos = { lat, lng };
            marker.setPosition(pos);
            map.setCenter(pos);
        }
    }
};

let geocodeTimer = null;
const geocodeAddress = () => {
    if (!geocoder || !map || !marker) return;
    
    clearTimeout(geocodeTimer);
    geocodeTimer = setTimeout(() => {
        const addrParts = [];
        if (form.value.ciudad) addrParts.push(form.value.ciudad);
        if (form.value.estadoDir) addrParts.push(form.value.estadoDir);
        addrParts.push("Venezuela");

        const address = addrParts.join(", ");
        if (addrParts.length <= 1) return; // Only "Venezuela" is not enough

        geocoder.geocode({ address }, (results, status) => {
            if (status === 'OK' && results[0]) {
                const pos = results[0].geometry.location;
                map.setCenter(pos);
                marker.setPosition(pos);
                form.value.coordenadasGps = `${pos.lat().toFixed(6)}, ${pos.lng().toFixed(6)}`;
            }
        });
    }, 1000);
};

// Watchers for location updates
watch(() => form.value.ciudad, geocodeAddress);
watch(() => form.value.estadoDir, geocodeAddress);

watch(() => props.initialData, () => {
    initData();
    setTimeout(initMap, 200);
});

onMounted(() => {
    fetchUsers();
    initData();
    setTimeout(initMap, 500);
});
</script>

<style scoped>
.sucursal-form-modern {
    display: flex;
    flex-direction: column;
    height: 100%;
    color: #1e293b;
}

.form-scroll-area {
    padding: 1.5rem;
    overflow-y: auto;
    flex: 1;
    max-height: calc(90vh - 120px);
}

.premium-form-card {
    background: white;
    padding: 1.75rem;
    border-radius: 20px;
    border: 1px solid #f1f5f9;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.02);
}

.card-badge-premium {
    display: inline-flex;
    padding: 0.4rem 0.75rem;
    background: #eff6ff;
    color: #3b82f6;
    border-radius: 10px;
    font-size: 0.75rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 1.5rem;
}

.card-badge-premium.operational {
    background: #f0fdf4;
    color: #16a34a;
}

.card-badge-premium.localization {
    background: #fffbeb;
    color: #d97706;
}

.card-badge-premium.staff {
    background: #faf5ff;
    color: #9333ea;
}

.grid-layout-premium {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1.25rem;
}

.full-col {
    grid-column: span 2;
}

.input-group-premium {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.input-group-premium label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.8rem;
    font-weight: 700;
    color: #64748b;
}

.input-group-premium input,
.input-group-premium select,
.input-group-premium textarea {
    width: 100%;
    padding: 0.85rem 1rem;
    border-radius: 12px;
    border: 1.5px solid #e2e8f0;
    background: #f8fafc;
    font-size: 0.95rem;
    font-weight: 500;
    transition: all 0.2s;
}

.input-group-premium input:focus,
.input-group-premium select:focus,
.input-group-premium textarea:focus {
    outline: none;
    border-color: #3b82f6;
    background: white;
    box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
}

.file-drop-zone {
    border: 2px dashed #e2e8f0;
    border-radius: 12px;
    padding: 1rem;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    cursor: pointer;
    color: #64748b;
    font-size: 0.9rem;
    transition: all 0.2s;
}

.file-drop-zone:hover {
    border-color: #3b82f6;
    background: #eff6ff;
    color: #2563eb;
}

/* Staff Selector */
.staff-selector-modern {
    background: #f8fafc;
    border-radius: 16px;
    border: 1px solid #e2e8f0;
    overflow: hidden;
}

.staff-search-box {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 1rem 1.25rem;
    background: white;
    border-bottom: 1px solid #e2e8f0;
}

.staff-search-box input {
    border: none;
    background: transparent;
    flex: 1;
    font-size: 0.95rem;
    font-weight: 500;
    outline: none;
}

.staff-list {
    max-height: 220px;
    overflow-y: auto;
    padding: 0.5rem;
}

.staff-item-modern {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 0.75rem 1rem;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.2s;
}

.staff-item-modern:hover {
    background: white;
}

.staff-item-modern.active {
    background: #eff6ff;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.02);
}

.staff-avatar {
    width: 36px;
    height: 36px;
    background: #3b82f6;
    color: white;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 800;
}

.staff-info {
    flex: 1;
    display: flex;
    flex-direction: column;
}

.s-name {
    font-weight: 700;
    font-size: 0.9rem;
}

.s-role {
    font-size: 0.75rem;
    color: #64748b;
    font-weight: 500;
}

.selection-indicator {
    display: flex;
    align-items: center;
    color: #3b82f6;
}

.indicator-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #e2e8f0;
}

/* Footer */
.premium-form-footer {
    padding: 1.5rem;
    background: white;
    border-top: 1px solid #f1f5f9;
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
}

.btn-ghost-premium {
    padding: 0.85rem 1.5rem;
    border-radius: 14px;
    border: 1.5px solid #e2e8f0;
    background: white;
    font-weight: 700;
    color: #64748b;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-ghost-premium:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
}

.btn-primary-premium {
    padding: 0.85rem 2rem;
    background: #2563eb;
    color: white;
    border: none;
    border-radius: 14px;
    font-weight: 700;
    cursor: pointer;
    box-shadow: 0 4px 12px rgba(37, 99, 235, 0.25);
    transition: all 0.2s;
}

.btn-primary-premium:hover {
    background: #1d4ed8;
    transform: translateY(-2px);
    box-shadow: 0 6px 15px rgba(37, 99, 235, 0.3);
}

.btn-primary-premium:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
}

.custom-scrollbar::-webkit-scrollbar {
    width: 5px;
}

.custom-scrollbar::-webkit-scrollbar-track {
    background: transparent;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
    background: #e2e8f0;
    border-radius: 10px;
}

.map-container-premium {
    width: 100%;
    height: 300px;
    background: #f1f5f9;
    border-radius: 16px;
    border: 1px solid #e2e8f0;
    overflow: hidden;
    box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.05);
}

.text-xs { font-size: 0.7rem; }

.mt-6 {
    margin-top: 1.5rem;
}

.spinner-icon {
    animation: spin 1s linear infinite;
}

@keyframes spin {
    from {
        transform: rotate(0deg);
    }

    to {
        transform: rotate(360deg);
    }
}
</style>
